import React from "react";
import { AlertCircle, AlertTriangle, ArrowUp, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/Button";

import CustomTooltip from "@/components/shared/CustomTooltip";
import { TONE } from "../constants";
import Chip from "./Chip";

// current / behind / unknown, rendered the same way in every column, so a
// reader learns the three shapes once and reads all three columns with them.
//
// `ahead` and `diverged` were added once the API could tell them apart from
// `behind`. Both are differences from the code that pressing Apply does not
// fix, which is the whole reason they cannot keep sharing amber with `behind`:
// a database on a newer version than the code used to render as "matches the
// code", and one carrying a revision this build has never heard of rendered as
// merely behind.
const STATE_TONE = {
    current: { tone: TONE.emerald, icon: <CheckCircle2 size={11} /> },
    behind: { tone: TONE.amber, icon: <AlertCircle size={11} /> },
    ahead: { tone: TONE.violet, icon: <ArrowUp size={11} /> },
    diverged: { tone: TONE.rose, icon: <AlertTriangle size={11} /> },
    unknown: { tone: TONE.slate, icon: <AlertCircle size={11} /> },
};

/**
 * `title` used to land on a native `title` attribute — the browser's own
 * tooltip, which does not wrap and turned a two-clause string like
 * "v16 — Outreach tables · behind the code" into one long, unstyled run-on
 * line. Routed through `CustomTooltip` instead: same content, the app's own
 * bubble, `max-w-xs` so long titles wrap rather than stretching off-screen.
 *
 * `title` may be a string or a JSX node. The callers in `../index.jsx`
 * (`schemaTitle` / `rlsTitle`) pass a node: a bold identity line naming the
 * version, then a muted line for where it stands, rather than joining both into
 * one sentence.
 */
const StateChip = ({ state, label, title, mono, onClick }) => {
    const { tone, icon } = STATE_TONE[state] ?? STATE_TONE.unknown;
    const chip = <Chip tone={tone} icon={icon} label={label} mono={mono} />;

    // A chip that opens a detail drawer has to be reachable by keyboard and
    // announce itself as a control. Without `onClick` it stays exactly what it
    // was: a static chip, no button semantics, no focus ring.
    const content = !onClick ? (
        chip
    ) : (
        <Button
            type="button"
            onClick={onClick}
            className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-tone-violet/60 cursor-pointer"
        >
            {chip}
        </Button>
    );

    if (!title) return content;
    return <CustomTooltip text={title}>{content}</CustomTooltip>;
};

export default StateChip;